import React, {useState, useEffect} from 'react';
import {Box, Button, Dialog, IconButton} from "@mui/material";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CloseIcon from '@mui/icons-material/Close';

const styles = {
    iconBtn: {
        height: "40px",
        width: "fit-content",
        margin: 1,
        color: "white",
        ":hover": {
            backgroundColor: "rgba(255, 255, 255, 0.08)"
        }
    },
    icon: {
        color: "white"
    }
};

/**
 * A modal component that displays the front and rear images of a check.
 * @param {boolean} openImgModal - The state of the modal.
 * @param {function} setOpenImgModal - A function to set the state of the modal.
 * @param {object} checkImages - The images of the check.
 * @param {string} checkSide - The side of the check to display.
 * @returns {JSX.Element}
 */
export default function DepositImgDialog({openImgModal, setOpenImgModal, checkImages, checkSide}) {
    const [imgSide, setImgSide] = useState(checkSide);
    const [imgScale, setImgScale] = useState(1);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({x: 0, y: 0});
    const [imgPosition, setImgPosition] = useState({x: 0, y: 0});

    // Change the image side
    const handleSideChange = () => imgSide === "frontImage" ? setImgSide("rearImage") : setImgSide("frontImage");

    // Handle the mouse down event on the image to drag it
    const handleMouseDown = (e) => {
        e.preventDefault();
        setIsDragging(true);
        setDragStart({x: e.clientX - imgPosition.x, y: e.clientY - imgPosition.y});
    };

    // Drag and set image position the mouse is moved
    const handleMouseMove = (e) => {
        if (isDragging) {
            setImgPosition({x: e.clientX - dragStart.x, y: e.clientY - dragStart.y});
        }
    };

    // Stop dragging the image when the mouse is released
    const handleMouseUp = () => {
        setIsDragging(false);
    };

    /**
     * Adjusts the zoom level of an image.
     * @param {number} direction - The direction of the zoom. Use -1 to zoom in and 1 to zoom out.
     */
    const handleImgZoom = (direction) => {
        if (direction === -1 && imgScale <= 2) {
            setImgScale(imgScale + 0.1)
        } else if (direction === 1 && imgScale >= 0.5) {
            setImgScale(imgScale - 0.1)
        }
    }

    // Reset the image to its original position and scale
    const handleResetImg = () => {
        setImgScale(1);
        setImgPosition({x: 0, y: 0});
    }

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        } else {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging]);

    return (
        <Dialog
            open={openImgModal}
            onClose={() => setOpenImgModal(false)}
            fullWidth
            closeAfterTransition
            maxWidth={"lg"}
            slotProps={{
                backdrop: {
                    style: {
                        backgroundColor: 'rgba(0, 0, 0, 0.9)'
                    }
                },
                paper: {
                    style: {
                        backgroundColor: 'transparent',
                        backgroundImage: 'none',
                        boxShadow: 'none',
                        overflow: 'hidden',
                        alignItems: "center"
                    }
                }
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    width: "100%"
                }}>
                <IconButton
                    aria-label="Close"
                    title="Close"
                    onClick={() => setOpenImgModal(false)}
                    sx={styles.iconBtn}
                >
                    <CloseIcon sx={styles.icon}/>
                </IconButton>
            </Box>
            <Box
                onMouseDown={handleMouseDown}
                onWheel={(e) => handleImgZoom(Math.sign(e.deltaY))}
                sx={{
                    cursor: isDragging ? 'grabbing' : 'grab',
                    objectFit: "contain",
                    overflow: "hidden",
                    position: "relative",
                    width: "100%",
                    height: "100%"
                }}
            >
                <img
                    src={`data:image/png;base64,${checkImages[imgSide]}`}
                    alt=""
                    style={{
                        objectFit: "contain",
                        height: "100%",
                        width: "100%",
                        transform: `scale(${imgScale}) translate(${imgPosition.x}px, ${imgPosition.y}px)`,
                        transformOrigin: "center"
                    }}
                />
            </Box>
            <Box>
                <Button
                    variant="contained"
                    onClick={handleSideChange}
                    sx={{
                        height: "40px",
                        width: "120px",
                        margin: 1
                    }}
                >
                    {imgSide === "frontImage" ? "View Rear" : "View Front"}
                </Button>
                <IconButton
                    onClick={() => handleImgZoom(-1)}
                    aria-label="Zoom In"
                    title="Zoom In"
                    sx={styles.iconBtn}
                >
                    <AddCircleOutlineIcon sx={styles.icon}/>
                </IconButton>
                <IconButton
                    onClick={() => handleImgZoom(1)}
                    aria-label="Zoom Out"
                    title="Zoom Out"
                    sx={styles.iconBtn}>
                    <RemoveCircleOutlineIcon sx={styles.icon}/>
                </IconButton>
                <IconButton
                    onClick={handleResetImg}
                    aria-label="Reset Image"
                    title="Reset Image"
                    sx={styles.iconBtn}>
                    <RestartAltIcon sx={styles.icon}/>
                </IconButton>
            </Box>
        </Dialog>
    );
}